import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatINR, formatDate, API } from "../lib/api";
import { toast } from "sonner";
import { PINNABLE_REPORTS } from "../lib/reportPins";
import { Truck, Users, Receipt, Download, TrendingUp, Pin } from "lucide-react";

const TABS = [
  { key: "vehicle-pl", label: "Vehicle P&L", icon: Truck },
  { key: "driver-vehicle", label: "Driver ↔ Vehicle", icon: Users },
  { key: "gst", label: "Quarterly GST", icon: Receipt },
];

export default function Reports() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("vehicle-pl");
  const [vpl, setVpl] = useState(null);
  const [dv, setDv] = useState(null);
  const [dvMode, setDvMode] = useState("by_vehicle");
  const [gst, setGst] = useState(null);
  const [gstYear, setGstYear] = useState("");
  const [vplSearch, setVplSearch] = useState("");
  const [dvSearch, setDvSearch] = useState("");
  const [pinned, setPinned] = useState([]);

  useEffect(() => {
    api.get("/dashboard/report-pins").then((r) => setPinned((r.data || []).map((p) => p.report_key))).catch(() => {});
  }, []);

  async function togglePin(key) {
    const isPinned = pinned.includes(key);
    try {
      if (isPinned) {
        await api.delete(`/dashboard/report-pins/${key}`);
        setPinned((p) => p.filter((k) => k !== key));
        toast.info("Removed from dashboard");
      } else {
        await api.post("/dashboard/report-pins", { report_key: key, title: PINNABLE_REPORTS[key].title });
        setPinned((p) => [...p, key]);
        toast.success("Pinned to dashboard");
      }
    } catch { toast.error("Could not update pin"); }
  }

  useEffect(() => {
    if (tab === "vehicle-pl" && !vpl) api.get("/reports/vehicle-pl").then((r) => setVpl(r.data)).catch(() => {});
    if (tab === "driver-vehicle" && !dv) api.get("/reports/driver-vehicle-history").then((r) => setDv(r.data)).catch(() => {});
    if (tab === "gst") api.get("/reports/gst-summary", { params: { year: gstYear || undefined } }).then((r) => setGst(r.data)).catch(() => {});
  }, [tab, gstYear]); // eslint-disable-line

  return (
    <div data-testid="reports-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Analytics</div>
        <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1">Reports</h1>
        <p className="text-sm text-[#68665E] mt-2">Profitability by vehicle, driver–vehicle history, and quarterly GST.</p>
      </div>

      <div className="flex gap-2 mb-6 border-b border-[#E5E3DB]">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`report-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === t.key ? "border-[#C84B31] text-[#1A1A18] font-medium" : "border-transparent text-[#68665E] hover:text-[#1A1A18]"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "vehicle-pl" && (
        <div data-testid="report-vehicle-pl">
          {vpl && (
            <>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <Stat label="Total Freight" value={formatINR(vpl.totals.freight)} />
                <Stat label="Total Expense" value={formatINR(vpl.totals.expense)} />
                <Stat label="Net Profit" value={formatINR(vpl.totals.profit)} accent={vpl.totals.profit >= 0} />
              </div>
              <div className="flex items-center gap-2 mb-4">
                <input className="input-field w-64" placeholder="Filter by vehicle number..." value={vplSearch} onChange={(e) => setVplSearch(e.target.value)} data-testid="vpl-search" />
                <PinButton pinned={pinned.includes("vehicle-pl")} onClick={() => togglePin("vehicle-pl")} testId="pin-vehicle-pl" />
              </div>
              <div className="table-wrap">
                <table className="w-full">
                  <thead><tr><th className="table-th">Vehicle</th><th className="table-th">LRs</th><th className="table-th">Freight</th><th className="table-th">Expense</th><th className="table-th">Profit</th></tr></thead>
                  <tbody>
                    {(() => {
                      const q = vplSearch.trim().toLowerCase();
                      const rows = vpl.rows.filter((r) => !q || r.vehicle_number.toLowerCase().includes(q));
                      return rows.length === 0 ? <tr><td className="table-td text-center text-[#68665E]" colSpan={5}>{q ? "No matching vehicles." : "No data yet."}</td></tr> :
                        rows.map((r) => (
                          <tr key={r.vehicle_number} className="table-row" data-testid={`vpl-row-${r.vehicle_number}`}>
                            <td className="table-td font-mono">{r.vehicle_number}</td>
                            <td className="table-td">{r.lr_count}</td>
                            <td className="table-td">{formatINR(r.freight)}</td>
                            <td className="table-td">{formatINR(r.expense)}</td>
                            <td className={`table-td font-medium ${r.profit >= 0 ? "text-[#2F6F4E]" : "text-[#8A2526]"}`}>{formatINR(r.profit)}</td>
                          </tr>
                        ));
                    })()}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "driver-vehicle" && dv && (
        <div data-testid="report-driver-vehicle">
          <div className="flex flex-wrap gap-2 mb-4 items-center">
            <button className={dvMode === "by_vehicle" ? "btn-primary" : "btn-secondary"} onClick={() => setDvMode("by_vehicle")} data-testid="dv-by-vehicle">By Vehicle</button>
            <button className={dvMode === "by_driver" ? "btn-primary" : "btn-secondary"} onClick={() => setDvMode("by_driver")} data-testid="dv-by-driver">By Driver</button>
            <input className="input-field w-56 ml-auto" placeholder={dvMode === "by_vehicle" ? "Filter by vehicle..." : "Filter by driver..."} value={dvSearch} onChange={(e) => setDvSearch(e.target.value)} data-testid="dv-search" />
          </div>
          <div className="space-y-3">
            {(() => {
              const q = dvSearch.trim().toLowerCase();
              const groups = (dvMode === "by_vehicle" ? dv.by_vehicle : dv.by_driver).filter((g) => !q || String(dvMode === "by_vehicle" ? g.vehicle_number : g.driver_name).toLowerCase().includes(q));
              return groups.length === 0 ? <div className="text-sm text-[#A09E96]">No matches.</div> : groups.map((g, i) => (
              <details key={`${(dvMode === "by_vehicle" ? g.vehicle_number : g.driver_name) || "grp"}-${i}`} className="bg-white border border-[#E5E3DB]" data-testid={`dv-group-${i}`}>
                <summary className="px-4 py-3 cursor-pointer flex items-center justify-between text-sm">
                  <span className="font-medium font-mono">{dvMode === "by_vehicle" ? g.vehicle_number : g.driver_name}</span>
                  <span className="text-[#68665E]">{dvMode === "by_vehicle" ? `${g.driver_count} driver(s)` : `${g.vehicle_count} vehicle(s)`} · {g.trip_count} trip(s)</span>
                </summary>
                <div className="px-4 pb-3">
                  <div className="text-xs text-[#68665E] mb-2">{dvMode === "by_vehicle" ? `Drivers: ${g.drivers.join(", ")}` : `Vehicles: ${g.vehicles.join(", ")}`}</div>
                  <div className="divide-y divide-[#F0EDE5]">
                    {g.trips.map((t, j) => (
                      <div key={t.lr_id || j} className="flex items-center justify-between py-1.5 text-sm">
                        <button className="text-[#C84B31] hover:underline font-mono text-xs" onClick={() => navigate(`/lr/${t.lr_id}`)}>{t.lr_number}</button>
                        <span className="flex-1 px-3 text-[#68665E]">{dvMode === "by_vehicle" ? t.driver_name : t.vehicle_number} · {t.route}</span>
                        <span className="text-xs text-[#A09E96]">{formatDate(t.lr_date)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </details>
            )); })()}
          </div>
        </div>
      )}

      {tab === "gst" && gst && (
        <div data-testid="report-gst">
          <div className="flex items-center justify-between mb-4">
            <select className="input-field w-40" value={gstYear} onChange={(e) => setGstYear(e.target.value)} data-testid="gst-year-filter">
              <option value="">All years</option>
              {gst.years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <div className="flex items-center gap-2">
              <PinButton pinned={pinned.includes("gst")} onClick={() => togglePin("gst")} testId="pin-gst" />
              <a href={`${API}/export/gst-summary.csv${gstYear ? `?year=${gstYear}` : ""}`} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="gst-export-csv"><Download className="w-4 h-4" /> Export CSV</a>
            </div>
          </div>
          <div className="table-wrap">
            <table className="w-full">
              <thead><tr><th className="table-th">Quarter</th><th className="table-th">Invoices</th><th className="table-th">Taxable Value</th><th className="table-th">GST (Forward)</th><th className="table-th">GST (RCM)</th><th className="table-th">Total</th></tr></thead>
              <tbody>
                {gst.rows.length === 0 ? <tr><td className="table-td text-center text-[#68665E]" colSpan={6}>No invoices in this period.</td></tr> :
                  gst.rows.map((r) => (
                    <tr key={r.quarter} className="table-row" data-testid={`gst-row-${r.quarter}`}>
                      <td className="table-td font-medium">{r.quarter}</td>
                      <td className="table-td">{r.invoice_count}</td>
                      <td className="table-td">{formatINR(r.taxable_value)}</td>
                      <td className="table-td">{formatINR(r.gst_forward)}</td>
                      <td className="table-td">{formatINR(r.gst_rcm)}</td>
                      <td className="table-td font-medium">{formatINR(r.total)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-white border border-[#E5E3DB] p-4">
      <div className="text-[11px] uppercase tracking-wide text-[#68665E] flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {label}</div>
      <div className={`text-xl font-semibold mt-1 ${accent === undefined ? "text-[#1A1A18]" : accent ? "text-[#2F6F4E]" : "text-[#8A2526]"}`}>{value}</div>
    </div>
  );
}

function PinButton({ pinned, onClick, testId }) {
  return (
    <button onClick={onClick} className={pinned ? "btn-primary" : "btn-secondary"} data-testid={testId} title="Show this report on your Dashboard">
      <Pin className="w-4 h-4" /> {pinned ? "Pinned to Dashboard" : "Pin to Dashboard"}
    </button>
  );
}
